import {
  Box,
  Button,
  IconButton,
  Modal,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import useStyles from "./styles";
import SelectFilter from "./selectFilter";
import DateFilter from "./dateFilter";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { GridCloseIcon } from "@mui/x-data-grid";
import {
  Add,
  Autorenew,
  Close,
  CloseFullscreen,
  DeleteOutline,
  FilterAltRounded,
  FilterList,
  Launch,
  ModeEditOutlineRounded,
  OpenInFull,
  PreviewOutlined,
  Refresh,
  RestartAltRounded,
  SaveOutlined,
} from "@mui/icons-material";
import { usePages } from "../../context/pageContext";
import { alpha, keyframes, Stack, useMediaQuery } from "@mui/system";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTemplate } from "../../context/templateContext";
import NewAlertModal from "../newAlertModal";
import { useFilters } from "../../context/filterContext";

interface CustomFiltersProps {
  paginaId: string;
  //handleFilters: (filters: FiltersSend) => void;
  scrolled: boolean;
  hideTopBar: boolean;
  filtersLoading: boolean;
  boxWidth: number | null;
  //cardsLoading: boolean;
  hideSideBar: boolean;
  setHideSideBar: React.Dispatch<React.SetStateAction<boolean>>;
  setHideTopBar: React.Dispatch<React.SetStateAction<boolean>>;
}

type OutletContextType = {
  isAutoRefresh: boolean;
};
export interface CustomFiltersRef {
  getFiltersState: () => any;
  setExternalFilter: (variable: string, value: any) => void;
}

const CustomFilters = forwardRef<CustomFiltersRef, CustomFiltersProps>(
  (
    {
      //  handleFilters,
      scrolled,
      hideTopBar,
      paginaId,
      filtersLoading,
      boxWidth,
      // cardsLoading,
      hideSideBar,
      setHideSideBar,
      setHideTopBar,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const { setFilterDate, filterDate } = usePages();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const theme = useTheme();
    const {
      editMode,
      setEditMode,
      onSave,
      onAddCard,
      onPreview,
      onExport,
      onDiscard,
      onBack,
      showBackButton,
      isTemplatePage,
      pageTitle,
      hasChanges,
    } = useTemplate();
    const { isAutoRefresh } = useOutletContext<OutletContextType>();
    const styles = useStyles(theme, hideTopBar, editMode);
    const isSm = useMediaQuery(theme.breakpoints.down("sm"));
    const [isFilterAplied, setIsFilterAplied] = useState<boolean>(true);
    const [visibleFiltersCount, setVisibleFiltersCount] = useState<
      number | null
    >(null);
    const [hasChanged, setHasChanged] = useState(false);

    const filterData = JSON.parse(localStorage.getItem("filters") || "{}");
    const [openDiscardModal, setOpenDiscardModal] = useState(false);

    const initialStateDate = useMemo(
      () => ({
        date: {
          timeStart: filterData[paginaId]?.date?.timeStart
            ? new Date(filterData[paginaId].date.timeStart)
            : new Date(new Date().setMonth(new Date().getMonth() - 1)),
          timeEnd: filterData[paginaId]?.date?.timeEnd
            ? new Date(filterData[paginaId].date.timeEnd)
            : new Date(),
          option: filterData[paginaId]?.date?.option ?? null,
        },
      }),
      [paginaId],
    );
    const {
      setFilters,
      filters: contextFilters,
      filtersConfig: filters,
    } = useFilters();

    useEffect(() => {
      if (contextFilters && Object.keys(contextFilters).length > 0) {
        setFilterState(parseStringToDate(contextFilters));
      }
    }, [contextFilters]);

    const hasDevPermission = (): boolean => {
      const data = localStorage.getItem("1");
      if (data) {
        try {
          const parsedData = JSON.parse(data);
          return (
            parsedData.usuarioCliente &&
            parsedData.usuarioCliente.codigoPermissao === "adm001"
          );
        } catch {
          return false;
        }
      }
      return false;
    };

    const parseStringToDate = (data: any) => {
      if (!data?.date) return data;

      return {
        ...data,
        date: {
          ...data.date,
          timeStart: new Date(data.date.timeStart),
          timeEnd: new Date(data.date.timeEnd),
        },
      };
    };
    const initialStateSelects = filters?.reduce(
      (acc, filter) => {
        if (filter.type === "select" && filter.variable) {
          acc[filter.variable] = filter.options ?? [];
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    const [filtersState, setFilterState] = useState<any>({
      ...initialStateSelects,
      ...initialStateDate,
    });

    useImperativeHandle(ref, () => ({
      getFiltersState: () => filtersState,
      setExternalFilter: (variable, value) => {
        setFilterState((prev: any) => ({
          ...prev,
          [variable]: value,
        }));
      },
    }));

    useEffect(() => {
      setOpenModal(false);
    }, [isSm]);

    useEffect(() => {
      const newStateSelects = filterData[paginaId]
        ? parseStringToDate(filterData[paginaId])
        : filters?.reduce(
            (acc, filter) => {
              if (filter.type === "select" && filter.variable) {
                acc[filter.variable] = filter.options
                  ? [...filter.options]
                  : [];
              }
              return acc;
            },
            {} as Record<string, any>,
          );

      setFilterState((prevState: Record<string, any>) => ({
        ...newStateSelects,
        date: filterData[paginaId]?.date
          ? parseStringToDate(filterData[paginaId]).date
          : prevState.date,
      }));
    }, [filters]);

    useEffect(() => {
      const count = Math.floor(
        (Number(boxWidth) - (editMode ? 580 : 405)) / 260,
      );

      setVisibleFiltersCount(Math.max(1, count));
    }, [boxWidth, editMode]);
    const handleChangeSelectFilter = (
      value: string[],
      filter: Filters,
      type?: "menu" | "tag",
    ) => {
      const isAllSelected = value.length === 0;
      const newValue =
        isAllSelected && type === "tag"
          ? initialStateSelects?.[filter.variable]
          : value;

      setFilterState((prev: Record<string, any>) => {
        const nextState = {
          ...prev,
          [filter.variable]: newValue,
        };

        if (type && type === "tag") {
          handleApplyFiltersWithParams(nextState);
        }

        return nextState;
      });

      setHasChanged(true);
      setIsFilterAplied(false);
    };

    const handleApplyFiltersWithParams = (params: any) => {
      const start = new Date(params.date.timeStart);
      start.setSeconds(0, 0);
      const end = new Date(params.date.timeEnd);
      end.setSeconds(0, 0);

      const payload: FiltersSend = {
        ...params,
        date: {
          timeStart: start.toISOString(),
          timeEnd: end.toISOString(),
          option: params?.date?.option || null,
        },
      };

      setFilters(payload);

      const currentFilters = JSON.parse(
        localStorage.getItem("filters") || "{}",
      );
      currentFilters[paginaId] = payload;
      localStorage.setItem("filters", JSON.stringify(currentFilters));
    };

    const handleChangeDateFilter = (
      valueParam: dateFilter,
      filter: Filters,
    ) => {
      const { timeStart, timeEnd, option } = valueParam ?? {
        timeStart: new Date(),
        timeEnd: new Date().getMonth() - 1,
        option: null,
      };

      setFilterState((data: Record<string, any>) => ({
        ...data,
        [filter.variable]: {
          timeStart: timeStart,
          timeEnd: timeEnd,
          option: option,
        },
      }));
      setHasChanged(true);
      setIsFilterAplied(valueParam === null ? true : false);
    };
    const handleResetButton = () => {
      setIsFilterAplied(false);
      setHasChanged(false);

      const resetState = {
        ...initialStateSelects,
        ...initialStateDate,
      };
      const formattedData = {
        ...initialStateSelects,
        date: {
          timeStart: initialStateDate.date.timeStart.toISOString(),
          timeEnd: initialStateDate.date.timeEnd.toISOString(),
          option: null,
        },
      };

      setFilters(formattedData);

      if (filterData[paginaId]) {
        delete filterData[paginaId];

        localStorage.setItem("filters", JSON.stringify(filterData));
      }
    };
    const handleApplyFilters = () => {
      setFilterDate(filtersState.date);
      setIsFilterAplied(true);

      const start = new Date(filtersState.date.timeStart);
      start.setSeconds(0, 0);
      const end = new Date(filtersState.date.timeEnd);
      end.setSeconds(0, 0);

      const filtersToApply = { ...filtersState };

      filters?.forEach((filter) => {
        if (filter.type === "select" && filter.variable) {
          const value = filtersState[filter.variable];

          if (Array.isArray(value) && value.length === 0) {
            filtersToApply[filter.variable] = filter.options ?? [];
          }
        }
      });

      const payload: FiltersSend = {
        ...filtersToApply,
        date: {
          timeStart: start.toISOString(),
          timeEnd: end.toISOString(),
          option: filtersState?.date?.option || null,
        },
      };

      setFilters(payload);

      filterData[paginaId] = payload;

      localStorage.setItem("filters", JSON.stringify(filterData));
    };

    const hasAdminPermission = (): boolean => {
      const data = localStorage.getItem("1");
      if (data) {
        try {
          const parsedData = JSON.parse(data);
          return (
            parsedData.usuarioCliente &&
            (parsedData.usuarioCliente.codigoPermissao === "adm001" ||
              parsedData.usuarioCliente.codigoPermissao === "adm002")
          );
        } catch {
          return false;
        }
      }
      return false;
    };
    const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
    return (
      <>
        <Box sx={styles.boxContainerFilters(scrolled)}>
          <Stack sx={styles.stacks}>
            {filters
              ?.slice(0, Number(visibleFiltersCount))
              .map((filter, index) =>
                filtersLoading ? (
                  <Skeleton
                    key={index + "skeleton"}
                    variant="rounded"
                    width="150px"
                    height="40px"
                    animation="wave"
                    sx={{
                      opacity: theme.palette.mode === "dark" ? 0.4 : 0.8,
                    }}
                  />
                ) : filter?.type === "select" && filter?.options ? (
                  <SelectFilter
                    key={index + filter.label}
                    label={filter.label}
                    options={filter.options}
                    value={filtersState[filter.variable]}
                    onChange={(newValue: string[], type?: string) =>
                      handleChangeSelectFilter(newValue, filter, type as "tag")
                    }
                    onClose={() => {
                      if (!isFilterAplied) handleApplyFilters();
                    }}
                    // disabled={cardsLoading}
                  />
                ) : (
                  filter?.type === "date" && (
                    <DateFilter
                      key={index}
                      value={filtersState[filter.variable]}
                      onChange={(value: dateFilter) =>
                        handleChangeDateFilter(value, filter)
                      }
                      onClose={() => {
                        if (!isFilterAplied) handleApplyFilters();
                      }}
                      // disabled={cardsLoading}
                    />
                  )
                ),
              )}{" "}
            <Button
              variant="contained"
              sx={styles.buttonFilter}
              onClick={() => {
                setOpenModal(true);
              }}
              startIcon={<FilterList />}
            >
              Filtros
            </Button>
            <Button
              startIcon={<Close />}
              variant="outlined"
              //disabled={!hasChanged}
              color="inherit"
              onClick={handleResetButton}
              sx={styles.buttonRestore}
              size="small"
            >
              Limpar
            </Button>
          </Stack>
          <Stack sx={{ ...styles.stacks, marginRight: 2 }}>
            {isAutoRefresh && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Autorenew
                  sx={{
                    animation: `${spin} 1.5s linear infinite`,
                    color: "primary.main",
                    fontSize: "1.8rem",
                  }}
                />
              </Box>
            )}
            <Tooltip
              title={
                hideSideBar && hideTopBar ? "Fechar Tela cheia " : "Tela Cheia"
              }
            >
              <IconButton
                sx={{
                  ...styles.buttonFilter,
                  display: { xs: "none", sm: "flex" },
                  maxWidth: "50px",
                }}
                disableRipple
                onClick={() => {
                  setHideSideBar(!hideSideBar);
                  setHideTopBar(!hideTopBar);
                }}
              >
                {hideSideBar && hideTopBar ? (
                  <OpenInFull />
                ) : (
                  <CloseFullscreen />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="Recarregar">
              <IconButton
                onClick={handleApplyFilters}
                sx={{
                  ...styles.buttonFilter,
                  display: { xs: "none", sm: "flex" },
                  maxWidth: "50px",
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            {isTemplatePage && hasAdminPermission() && (
              <Box sx={styles.buttonsContainer}>
                {editMode ? (
                  <>
                    <Tooltip title="Descartar">
                      <IconButton
                        sx={{
                          ...styles.buttonFilter,
                          maxWidth: "50px",
                          color:
                            theme.palette.mode === "dark"
                              ? theme.palette.error.main
                              : theme.palette.error.dark,
                          backgroundColor: alpha(
                            theme.palette.background.paper,
                            1,
                          ),
                        }}
                        onClick={() => setOpenDiscardModal(true)}
                        disabled={!hasChanges}
                        color="error"
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Adicionar">
                      <IconButton
                        sx={{
                          ...styles.buttonFilter,
                          maxWidth: "50px",
                          color: theme.palette.primary.main,
                        }}
                        onClick={() => onAddCard && onAddCard()}
                      >
                        <Add />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Salvar">
                      <IconButton
                        sx={{
                          ...styles.buttonFilter,
                          maxWidth: "50px",
                          color: theme.palette.primary.main,
                        }}
                        onClick={onSave ? onSave : () => {}}
                        disabled={!hasChanges}
                      >
                        <SaveOutlined />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Visualizar">
                      <IconButton
                        sx={{
                          ...styles.buttonFilter,
                          maxWidth: "50px",
                          color: theme.palette.primary.main,
                        }}
                        onClick={onPreview ? onPreview : () => {}}
                      >
                        <PreviewOutlined />
                      </IconButton>
                    </Tooltip>
                    {/* <Button
                      variant="text"
                      startIcon={<DeleteOutline />}
                      sx={styles.deleteButton}
                      onClick={() => setOpenDiscardModal(true)}
                      disabled={!hasChanges}
                      size="small"
                      color="error"
                    >
                      Descartar
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<Add />}
                      sx={styles.button}
                      onClick={() => onAddCard && onAddCard()}
                      size="small"
                    >
                      Adicionar
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<SaveOutlined />}
                      sx={styles.button}
                      onClick={onSave ? onSave : () => {}}
                      disabled={!hasChanges}
                      size="small"
                    >
                      Salvar
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<PreviewOutlined />}
                      sx={styles.button}
                      onClick={onPreview ? onPreview : () => {}}
                      size="small"
                    >
                      Visualizar
                    </Button> */}
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => setEditMode(true)}
                    sx={styles.button}
                    startIcon={<ModeEditOutlineRounded />}
                    size="small"
                  >
                    Editar
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        </Box>

        <Box sx={styles.boxContainerMobile}>
          <Button
            variant="contained"
            sx={styles.buttonMobile}
            onClick={() => {
              setOpenModal(true);
            }}
            startIcon={<FilterList />}
          >
            Filtros
          </Button>
          {isTemplatePage && hasAdminPermission() && (
            <Box
              sx={{
                ...styles.buttonsContainer,
                flex: 1,
              }}
            >
              {isAutoRefresh && !editMode && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Autorenew
                    sx={{
                      animation: `${spin} 1.5s linear infinite`,
                      color: "primary.main",
                      fontSize: "1.8rem",
                    }}
                  />
                </Box>
              )}
              {editMode ? (
                <>
                  <Tooltip title="Descartar">
                    <IconButton
                      sx={{
                        ...styles.buttonFilter,
                        maxWidth: "50px",
                        color:
                          theme.palette.mode === "dark"
                            ? theme.palette.error.main
                            : theme.palette.error.dark,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          1,
                        ),
                      }}
                      onClick={() => setOpenDiscardModal(true)}
                      disabled={!hasChanges}
                      color="error"
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Adicionar">
                    <IconButton
                      sx={{
                        ...styles.buttonFilter,
                        maxWidth: "50px",
                        color: theme.palette.primary.main,
                      }}
                      onClick={() => onAddCard && onAddCard()}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Salvar">
                    <IconButton
                      sx={{
                        ...styles.buttonFilter,
                        maxWidth: "50px",
                        color: theme.palette.primary.main,
                      }}
                      onClick={onSave ? onSave : () => {}}
                      disabled={!hasChanges}
                    >
                      <SaveOutlined />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Visualizar">
                    <IconButton
                      sx={{
                        ...styles.buttonFilter,
                        maxWidth: "50px",
                        color: theme.palette.primary.main,
                      }}
                      onClick={onPreview ? onPreview : () => {}}
                    >
                      <PreviewOutlined />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Editar">
                  <IconButton
                    onClick={() => setEditMode(true)}
                    sx={{
                      ...styles.buttonFilter,
                      maxWidth: "50px",
                      color: theme.palette.primary.main,
                    }}
                  >
                    <ModeEditOutlineRounded />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <Tooltip
            title={
              hideSideBar && hideTopBar ? "Tela Cheia" : "Fechar tela cheia"
            }
          >
            <IconButton
              sx={{
                ...styles.buttonFilter,
                maxWidth: "50px",
                flex: 1,
                color: theme.palette.primary.main,
              }}
              disableRipple
              onClick={() => {
                setHideSideBar(!hideSideBar);
                setHideTopBar(!hideTopBar);
              }}
              color={theme.palette.primary.main as "primary"}
            >
              {/* <MenuOpen /> */}

              {hideSideBar && hideTopBar ? <OpenInFull /> : <CloseFullscreen />}
            </IconButton>
          </Tooltip>
        </Box>
        <Modal
          open={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={styles.modal}
        >
          <Paper sx={styles.paperModal}>
            <Box sx={styles.boxModalHeader}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="h6" component="h2">
                  Filtros
                </Typography>
                {hasDevPermission() && (
                  <IconButton
                    onClick={() =>
                      navigate(`/configuracoes/filtros/${paginaId}`)
                    }
                  >
                    <Launch sx={{ width: "1rem" }} />
                  </IconButton>
                )}
              </Box>

              <IconButton
                onClick={() => {
                  setOpenModal(false);
                }}
              >
                <GridCloseIcon />
              </IconButton>
            </Box>
            <Box sx={styles.boxModalFilters}>
              {filters?.map((filter, index) => {
                return filter?.type === "select" && filter?.options ? (
                  <SelectFilter
                    key={index + filter.label + "mobile"}
                    label={filter.label}
                    options={filter.options}
                    value={filtersState[filter.variable]}
                    onChange={(newValue: string[], type?: string) =>
                      handleChangeSelectFilter(newValue, filter, type as "tag")
                    }
                    onClose={() => {}}
                  />
                ) : (
                  filter?.type === "date" && (
                    <DateFilter
                      key={index + "mobile"}
                      value={filtersState[filter.variable]}
                      onChange={(value: dateFilter) =>
                        handleChangeDateFilter(value, filter)
                      }
                      onClose={() => {}}
                    />
                  )
                );
              })}
            </Box>
            <Box sx={styles.boxModalFooter}>
              <Button
                variant="outlined"
                onClick={() => {
                  // setOpenModal(false);
                  handleResetButton();
                }}
                startIcon={<RestartAltRounded />}
              >
                Restaurar
              </Button>
              <Button
                variant="contained"
                startIcon={<FilterAltRounded />}
                disabled={isFilterAplied}
                onClick={() => {
                  handleApplyFilters();
                  setOpenModal(false);
                }}
              >
                Filtrar
              </Button>
            </Box>
          </Paper>
        </Modal>
        <NewAlertModal
          openModal={openDiscardModal}
          setOpenModal={setOpenDiscardModal}
          handleButton={() => {
            setOpenDiscardModal(false);
            if (onDiscard) onDiscard();
          }}
          handleCancel={() => setOpenDiscardModal(false)}
          severity="warning"
          textContent="Tem certeza que deseja descartar as alterações?"
          singleButton={false}
          buttonText="Descartar"
          buttonCancelText="Cancelar"
        />
      </>
    );
  },
);
export default CustomFilters;
